/**
 * Northstar composition layout tokens — the single source of truth for
 * every spatial value the Northstar scene depends on.
 *
 * Course-correction 2026-02 (Stage 3 Refinement):
 *   The scene must be *composed*, not mathematically distributed. Every
 *   element in `<NorthstarScene>` derives its position from these tokens
 *   so the composition is deterministic, mirrored, and can be reasoned
 *   about as a grid rather than as a list of ad-hoc offsets.
 *
 * Tokens per tier:
 *   axisX / axisY        — the authoritative central axis (fraction of vp)
 *   portraitSize         — diameter of the patient photograph
 *   portraitHaloExtend   — extra radius (px) of mint edge halo around photo
 *   columnX              — |x| distance from axis to the domain-column center
 *   rowStep              — vertical spacing between adjacent domain rows
 *   rowYs                — explicit 5-row Y offsets (used on stacked mobile
 *                          tiers where the portrait steals the center band)
 *   emblemSize           — diameter of every emblem badge
 *   emblemTextGap        — horizontal gap between emblem and the text block
 *   textMaxW             — max width of the domain name + description block
 *   nameToDescGap        — vertical gap between domain name and description
 *   nameSize / descSize  — Tailwind text-size class tokens
 *   ringOuter            — outermost visible ring radius (used to size the
 *                          ring SVG canvas)
 *   matrixInner /
 *   matrixOuter          — horizontal attenuation radii for MatrixField
 *                          (as fraction of viewport width)
 *   labelStack           — "beside" | "below" — how emblem+text stack
 *   descAtRest           — "always" | "focus-only"
 *   headerLayout         — "horizontal" | "stacked"
 *
 * The tokens are indexed by tier name; `useNorthstarSizing` is a thin
 * wrapper that returns the active tier row + raw viewport dimensions.
 */

// Portrait scale is a multiplier applied to the ring family; the whole
// environment scales as one unit so nothing drifts independently.
export const RING_SCALE_R2 = 2.29;
export const RING_SCALE_R5 = 5.14;

/** @type {Record<string, any>} */
export const NORTHSTAR_TIERS = {
  "desktop-lg": {
    portraitSize:       360,
    portraitHaloExtend: 50,
    columnX:            410,
    rowStep:            152,
    rowYs:              null,   // derived from rowStep
    emblemSize:         86,
    emblemTextGap:      22,
    textMaxW:           218,
    nameToDescGap:      4,
    nameSize:           "text-lg",
    descSize:           "text-sm",
    matrixInner:        0.16,
    matrixOuter:        0.52,
    labelStack:         "beside",
    descAtRest:         "always",
    headerLayout:       "horizontal",
    headerReserve:      66,
    sceneShiftY:        18,
  },
  "desktop": {
    portraitSize:       260,
    portraitHaloExtend: 36,
    columnX:            340,
    rowStep:            132,
    rowYs:              null,
    emblemSize:         78,
    emblemTextGap:      18,
    textMaxW:           192,
    nameToDescGap:      4,
    nameSize:           "text-base",
    descSize:           "text-sm",
    matrixInner:        0.16,
    matrixOuter:        0.52,
    labelStack:         "beside",
    descAtRest:         "always",
    headerLayout:       "horizontal",
    headerReserve:      66,
    sceneShiftY:        16,
  },
  "tablet": {
    portraitSize:       260,
    portraitHaloExtend: 36,
    columnX:            282,
    rowStep:            134,
    rowYs:              null,
    emblemSize:         74,
    emblemTextGap:      16,
    textMaxW:           158,
    nameToDescGap:      3,
    nameSize:           "text-base",
    descSize:           "text-xs",
    matrixInner:        0.18,
    matrixOuter:        0.54,
    labelStack:         "beside",
    descAtRest:         "always",
    headerLayout:       "horizontal",
    headerReserve:      64,
    // Lift the tablet scene up so the empty top band collapses.
    sceneShiftY:       -110,
  },
  "narrow": {
    portraitSize:       200,
    portraitHaloExtend: 32,
    columnX:            112,
    rowStep:            null,
    // Stacked columns skip the vertical band the portrait occupies.
    rowYs:              [-322, -212, 208, 320, 432],
    emblemSize:         58,
    emblemTextGap:      0,
    textMaxW:           148,
    nameToDescGap:      2,
    nameSize:           "text-base",
    descSize:           "text-xs",
    matrixInner:        0.24,
    matrixOuter:        0.58,
    labelStack:         "below",
    descAtRest:         "always",
    headerLayout:       "stacked",
    headerReserve:      120,
    sceneShiftY:        62,
  },
  "mobile": {
    // Stage 3.7 — mobile bounding-box containment recomputation.
    // Available scene height (780 vp − 150 header − 24 safe) = 606 px.
    // Composition slots:
    //   Row 0 (Legal/Wellness)      abs y ≈ 214
    //   Row 1 (Housing/Trainers)    abs y ≈ 302
    //   Portrait 140 px (center 452)
    //   Name (text-sm) + Subtitle (text-[11px])
    //   Row 2 (Immigration/Coaches) abs y ≈ 600
    //   Row 3 (Retail/Therapists)   abs y ≈ 668
    //   Row 4 (Nourishment/Aesth.)  abs y ≈ 736 (block bottom ≤ 780)
    portraitSize:       140,
    portraitHaloExtend: 26,
    columnX:            96,
    rowStep:            null,
    rowYs:              [-238, -150, 148, 214, 278],
    emblemSize:         48,
    emblemTextGap:      0,
    textMaxW:           118,
    nameToDescGap:      2,
    nameSize:           "text-sm",
    descSize:           "text-[11px]",
    matrixInner:        0.28,
    matrixOuter:        0.60,
    labelStack:         "below",
    descAtRest:         "focus-only",
    headerLayout:       "stacked",
    headerReserve:      150,
    sceneShiftY:        62,
  },
};

/** Pick the tier name for a given viewport width. */
export function tierNameFor(vw) {
  if (vw >= 1280) return "desktop-lg";
  if (vw >= 1024) return "desktop";
  if (vw >= 768)  return "tablet";
  if (vw >= 520)  return "narrow";
  return "mobile";
}

/** Get the tokens for a viewport width, with rowYs derived if not set. */
export function tokensFor(vw) {
  const name = tierNameFor(vw);
  const t = { name, ...NORTHSTAR_TIERS[name] };
  if (!t.rowYs) {
    const step = t.rowStep;
    t.rowYs = [-2 * step, -1 * step, 0, 1 * step, 2 * step];
  }
  return t;
}
